/**
 * WebSocket Service - Frontend
 * Handles real-time communication with the backend
 */

import { io, Socket } from 'socket.io-client';
import { toast } from 'sonner';

type WorkspaceUpdateHandler = (data: Record<string, unknown>) => void;
type AlertHandler = (alert: Record<string, unknown>) => void;
type TaskUpdateHandler = (task: Record<string, unknown>) => void;
type CaseUpdateHandler = (caseData: Record<string, unknown>) => void;
type PublicationUpdateHandler = (publication: Record<string, unknown>) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private workspaceUpdateHandlers: Set<WorkspaceUpdateHandler> = new Set();
  private alertHandlers: Set<AlertHandler> = new Set();
  private taskUpdateHandlers: Set<TaskUpdateHandler> = new Set();
  private caseUpdateHandlers: Set<CaseUpdateHandler> = new Set();
  private publicationUpdateHandlers: Set<PublicationUpdateHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isConnecting = false;

  /**
   * Connect to WebSocket server
   */
  connect(token: string): void {
    if (this.socket?.connected) {
      console.log('WebSocket already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('WebSocket connection in progress');
      return;
    }

    this.isConnecting = true;

    // Em dev, usa o hostname atual para funcionar de outros dispositivos na rede
    // Em produção, usa URL relativa (Nginx faz proxy de /socket.io/)
    const serverUrl = import.meta.env.DEV
      ? `${window.location.protocol}//${window.location.hostname}:4000`
      : (import.meta.env.VITE_API_URL || window.location.origin);

    this.socket = io(serverUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts
    });

    this.setupEventListeners();
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected', this.socket?.id);
      this.isConnecting = false;
      this.reconnectAttempts = 0;

      // Subscribe to workspace updates
      this.socket?.emit('workspace:subscribe');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
      this.isConnecting = false;

      // Se foi desconexão por cliente (logout/token expirado), não reconectar
      if (reason === 'io client disconnect') {
        console.log('🔒 Client disconnect - not reconnecting');
        return;
      }

      if (reason === 'io server disconnect') {
        // Server initiated disconnect, pode ser token expirado
        // Não reconectar automaticamente para evitar loops
        console.log('🔒 Server disconnect - not reconnecting (possible auth issue)');
        return;
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      this.isConnecting = false;
      this.reconnectAttempts++;

      // Se chegou ao limite de tentativas, parar silenciosamente
      // (não mostrar toast para não incomodar usuário)
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.log('🔇 Max reconnection attempts reached - giving up silently');
        this.disconnect();
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`🔄 WebSocket reconnected after ${attemptNumber} attempts`);
      toast.success('Conexão em tempo real restaurada');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 WebSocket reconnection attempt ${attemptNumber}`);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('WebSocket reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ WebSocket reconnection failed');
      toast.error('Não foi possível restaurar conexão em tempo real');
    });

    // Workspace events
    this.socket.on('workspace:update', (data) => {
      console.log('📡 Workspace update received:', data.type);
      this.workspaceUpdateHandlers.forEach(handler => handler(data));
    });

    this.socket.on('workspace:alert', (alert) => {
      console.log('🚨 Alert received:', alert.type);
      this.alertHandlers.forEach(handler => handler(alert));

      // Show toast notification for critical alerts
      if (alert.priority === 1) { // CRITICAL
        toast.error(alert.title, {
          description: alert.description,
          duration: 10000
        });
      } else if (alert.priority === 2) { // HIGH
        toast.warning(alert.title, {
          description: alert.description,
          duration: 7000
        });
      }
    });

    this.socket.on('workspace:task-update', (data) => {
      console.log('📋 Task update received:', data.task.id);
      this.taskUpdateHandlers.forEach(handler => handler(data.task));
    });

    this.socket.on('workspace:case-update', (data) => {
      console.log('📂 Case update received:', data.case.id);
      this.caseUpdateHandlers.forEach(handler => handler(data.case));
    });

    this.socket.on('workspace:publication-update', (data) => {
      console.log('📄 Publication update received:', data.publication.id);
      this.publicationUpdateHandlers.forEach(handler => handler(data.publication));
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.emit('workspace:unsubscribe');
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.clearHandlers();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Subscribe to workspace updates
   */
  onWorkspaceUpdate(handler: WorkspaceUpdateHandler): () => void {
    this.workspaceUpdateHandlers.add(handler);
    return () => this.workspaceUpdateHandlers.delete(handler);
  }

  /**
   * Subscribe to alerts
   */
  onAlert(handler: AlertHandler): () => void {
    this.alertHandlers.add(handler);
    return () => this.alertHandlers.delete(handler);
  }

  /**
   * Subscribe to task updates
   */
  onTaskUpdate(handler: TaskUpdateHandler): () => void {
    this.taskUpdateHandlers.add(handler);
    return () => this.taskUpdateHandlers.delete(handler);
  }

  /**
   * Subscribe to case updates
   */
  onCaseUpdate(handler: CaseUpdateHandler): () => void {
    this.caseUpdateHandlers.add(handler);
    return () => this.caseUpdateHandlers.delete(handler);
  }

  /**
   * Subscribe to publication updates
   */
  onPublicationUpdate(handler: PublicationUpdateHandler): () => void {
    this.publicationUpdateHandlers.add(handler);
    return () => this.publicationUpdateHandlers.delete(handler);
  }

  /**
   * Clear all handlers
   */
  private clearHandlers(): void {
    this.workspaceUpdateHandlers.clear();
    this.alertHandlers.clear();
    this.taskUpdateHandlers.clear();
    this.caseUpdateHandlers.clear();
    this.publicationUpdateHandlers.clear();
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
